// Sales "My quotations" (this task's brief) — every quotation this
// salesperson has priced, filterable by status, showing seal state.
// Ledger-style: one ruled table, no card-per-row (root CLAUDE.md).
// Filtering is client-side over the whole list, matching this codebase's
// own AllProformasPage/AllRequestsPage convention (see
// hooks/useSalesQuotations.js#useMyQuotations's own comment).

import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext.jsx';
import { useMyQuotations } from '../hooks/useSalesQuotations.js';
import LoadingState from '../components/LoadingState.jsx';
import ErrorState from '../components/ErrorState.jsx';
import EmptyState from '../components/EmptyState.jsx';
import { statusLabel } from '../i18n.js';
import { formatDate } from '../utils/formatDate.js';
import { formatMoney } from 'shared/money.js';
import styles from './MyQuotationsPage.module.css';

const STATUSES = [
  'draft',
  'pending_approval',
  'rejected',
  'approved',
  'sent',
  'accepted',
  'declined',
  'expired',
  'superseded',
];

function sealLabel(row, t) {
  if (!Boolean(row.sealed)) return '—';
  return row.unsealed_at ? t('myQuotations.seal.unsealed') : t('myQuotations.seal.sealed');
}

export default function MyQuotationsPage() {
  const { t, lang } = useLanguage();
  const navigate = useNavigate();
  const { data: quotations, isLoading, isError, error, refetch } = useMyQuotations();
  const [statusFilter, setStatusFilter] = useState('all');

  const filtered = useMemo(() => {
    if (!quotations) return [];
    if (statusFilter === 'all') return quotations;
    return quotations.filter((q) => q.status === statusFilter);
  }, [quotations, statusFilter]);

  const isTrulyEmpty = !isLoading && !isError && (quotations || []).length === 0;
  const isFilteredEmpty = !isTrulyEmpty && !isLoading && !isError && filtered.length === 0;

  return (
    <div className={styles.page}>
      <h1 className={styles.title}>{t('myQuotations.title')}</h1>
      <p className={styles.subtitle}>{t('myQuotations.subtitle')}</p>

      {!isTrulyEmpty && (
        <div className={styles.filterRow}>
          <span className={styles.filterLabel}>{t('myQuotations.filter.status')}</span>
          <select
            className={styles.filterSelect}
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <option value="all">{t('myQuotations.filter.all')}</option>
            {STATUSES.map((s) => (
              <option key={s} value={s}>
                {statusLabel(s, lang)}
              </option>
            ))}
          </select>
        </div>
      )}

      {isLoading && <LoadingState variant="rows" rows={6} />}

      {isError && !isLoading && (
        <ErrorState messageId="myQuotations.errorBody" error={error} onRetry={refetch} />
      )}

      {isTrulyEmpty && (
        <EmptyState titleId="myQuotations.emptyTitle" bodyId="myQuotations.emptyBody" />
      )}

      {isFilteredEmpty && <p className={styles.filterLabel}>{t('myQuotations.filter.all')}: 0</p>}

      {!isLoading && !isError && filtered.length > 0 && (
        <table className={styles.table}>
          <thead>
            <tr className={styles.headerRow}>
              <th>{t('myQuotations.col.proforma')}</th>
              <th>{t('myQuotations.col.organization')}</th>
              <th>{t('myQuotations.col.status')}</th>
              <th>{t('myQuotations.col.seal')}</th>
              <th>{t('myQuotations.col.total')}</th>
              <th>{t('myQuotations.col.validUntil')}</th>
              <th aria-hidden="true" />
            </tr>
          </thead>
          <tbody>
            {filtered.map((q) => (
              <tr
                key={q.id}
                className={styles.row}
                tabIndex={0}
                role="link"
                onClick={() => navigate(`/my-quotations/${q.id}`)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') navigate(`/my-quotations/${q.id}`);
                }}
              >
                <td className={`${styles.refCell} tabularNums`}>
                  {q.proforma_no || t('allProformas.notAllocated')}
                </td>
                <td>
                  {lang === 'am' && q.organization_name_am
                    ? q.organization_name_am
                    : q.organization_name}
                </td>
                <td>{statusLabel(q.status, lang)}</td>
                <td className={styles.sealBadge}>{sealLabel(q, t)}</td>
                <td className="tabularNums">{formatMoney(q.total)}</td>
                <td className="tabularNums">{formatDate(q.valid_until, lang) || '—'}</td>
                <td className={styles.openCell}>{t('queue.open')} →</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
